import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/context/AuthContext";
import {
  Shield, AlertTriangle, CheckCircle, Activity, Users, Lock, Calendar, ArrowLeft,
  TrendingDown, Filter, Loader2, Download, FileSpreadsheet, Mail,
} from "lucide-react";

/**
 * AuditLog · Dashboard de Seguridad TIC
 * Página admin con métricas de los últimos N días sobre el `audit_log`:
 * - KPIs (total, denegados, ratio)
 * - Top-5 usuarios con más denegaciones
 * - Top módulos+scope denegados
 * - Distribución por scope (read/write/delete)
 * - Evolución diaria (granted vs denied)
 * - Tabla detallada de últimas N entradas con filtros
 */

const fmtInt = (n) => Number(n || 0).toLocaleString("es-ES");
const SCOPE_LABEL = { read: "Lectura", write: "Edición", delete: "Borrado" };
const SCOPE_BG = {
  read:  "bg-blue-100 text-blue-700 border-blue-200",
  write: "bg-amber-100 text-amber-700 border-amber-200",
  delete:"bg-red-100 text-red-700 border-red-200",
};

export default function AuditLog() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [grantedFilter, setGrantedFilter] = useState("denied"); // "all" | "granted" | "denied"
  const [moduleFilter, setModuleFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, log] = await Promise.all([
        axios.get(`${API}/admin/audit-log/stats`, { params: { days } }),
        axios.get(`${API}/admin/audit-log`, {
          params: {
            limit: 200,
            ...(grantedFilter !== "all" ? { granted: grantedFilter === "granted" } : {}),
            ...(moduleFilter ? { module_id: moduleFilter } : {}),
            ...(userFilter ? { user_email: userFilter } : {}),
          },
        }),
      ]);
      setStats(s.data);
      setItems(log.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [days, grantedFilter, moduleFilter, userFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Export CSV/XLSX manteniendo los filtros activos. Usa fetch porque axios y
  // los blobs Excel pueden chocar con headers Authorization en algunos navegadores.
  const [exporting, setExporting] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const sendWeeklyEmailNow = async () => {
    if (!window.confirm("¿Enviar AHORA el audit log semanal por email a David Redondo (dredondo@valorizasm.com)?\n\nEsto adelanta el envío programado para el próximo lunes 8:10 UTC.")) return;
    setSendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/admin/audit-log/send-weekly-email`);
      alert(`✓ Email enviado vía Mandrill\n\n${data.rows} eventos · ${data.denied}/${data.total} denegados\n\nRevisa la bandeja en unos segundos (Mandrill cola los adjuntos).`);
    } catch (e) {
      alert("Error enviando email: " + (e.response?.data?.detail || e.message));
    } finally {
      setSendingEmail(false);
    }
  };

  const exportLog = async (fmt) => {
    setExporting(fmt);
    try {
      const params = new URLSearchParams({
        format: fmt,
        days: String(days),
        limit: "10000",
      });
      if (grantedFilter !== "all") params.set("granted", grantedFilter === "granted" ? "true" : "false");
      if (moduleFilter) params.set("module_id", moduleFilter);
      if (userFilter) params.set("user_email", userFilter);
      const token = localStorage.getItem("valoriza_token") || "";
      const resp = await fetch(`${API}/admin/audit-log/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // intenta extraer filename del Content-Disposition
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `audit_log_valoriza.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Error exportando audit log: " + err.message);
    } finally {
      setExporting("");
    }
  };

  const maxDailyEvent = Math.max(
    1,
    ...(stats?.daily || []).map(d => Math.max(d.granted, d.denied))
  );

  return (
    <div className="space-y-6 w-full" data-testid="audit-log-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin" className="text-xs text-gray-400 hover:text-[#007934] flex items-center gap-1 mb-1">
            <ArrowLeft size={11} /> Volver a Administración
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield size={22} className="text-[#007934]" /> Dashboard de Seguridad TIC · Audit Log
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Eventos de control de accesos R/W/D. Cada intento (concedido o denegado) queda registrado para auditoría.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1.5">
            <Calendar size={14} className="text-gray-400" />
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="audit-days-select"
              className="text-sm focus:outline-none bg-transparent"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={14}>Últimos 14 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={60}>Últimos 60 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
          </div>
          <button
            onClick={() => exportLog("csv")}
            disabled={exporting !== ""}
            data-testid="audit-export-csv"
            className="text-xs px-3 py-2 rounded-lg border border-gray-200 hover:border-[#007934] hover:bg-green-50 hover:text-[#007934] flex items-center gap-1.5 disabled:opacity-50"
            title="Descargar CSV con los filtros actuales (apto para hojas de cálculo)"
          >
            {exporting === "csv" ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} CSV
          </button>
          <button
            onClick={() => exportLog("xlsx")}
            disabled={exporting !== ""}
            data-testid="audit-export-xlsx"
            className="text-xs px-3 py-2 rounded-lg border border-[#007934] bg-[#007934] text-white hover:bg-[#00622a] flex items-center gap-1.5 disabled:opacity-50"
            title="Descargar Excel con cabecera Valoriza (apto para auditoría externa)"
          >
            {exporting === "xlsx" ? <Loader2 size={12} className="animate-spin"/> : <FileSpreadsheet size={12}/>} Excel
          </button>
          <button
            onClick={sendWeeklyEmailNow}
            disabled={sendingEmail}
            data-testid="audit-send-email"
            className="text-xs px-3 py-2 rounded-lg border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 flex items-center gap-1.5 disabled:opacity-50"
            title="Envía AHORA el dossier semanal a David Redondo (programado cada lunes 8:10 UTC)"
          >
            {sendingEmail ? <Loader2 size={12} className="animate-spin"/> : <Mail size={12}/>} Enviar semanal
          </button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl loading-shimmer"/>)}
        </div>
      ) : stats && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-kpis">
            <KPI icon={Activity} color="text-gray-600" bg="bg-gray-100"
                 label="Eventos totales" value={fmtInt(stats.totals.total)}/>
            <KPI icon={CheckCircle} color="text-green-700" bg="bg-green-100"
                 label="Concedidos" value={fmtInt(stats.totals.granted)}/>
            <KPI icon={AlertTriangle} color="text-red-700" bg="bg-red-100"
                 label="Denegados" value={fmtInt(stats.totals.denied)}/>
            <KPI icon={TrendingDown} color="text-purple-700" bg="bg-purple-100"
                 label="% Denegación" value={`${stats.totals.denied_pct}%`}/>
          </div>

          {/* Distribución por scope */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-4 lg:col-span-1" data-testid="audit-by-scope">
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                <Lock size={14} className="text-[#007934]"/> Denegados por scope
              </h3>
              <div className="space-y-2">
                {["read", "write", "delete"].map(sc => {
                  const total = stats.by_scope.read + stats.by_scope.write + stats.by_scope.delete || 1;
                  const pct = Math.round((stats.by_scope[sc] / total) * 100);
                  return (
                    <div key={sc}>
                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                        <span className={`font-bold uppercase tracking-wider ${
                          sc === "read"  ? "text-blue-700" :
                          sc === "write" ? "text-amber-700" : "text-red-700"
                        }`}>{SCOPE_LABEL[sc]}</span>
                        <span className="text-gray-500 font-mono">{fmtInt(stats.by_scope[sc])} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded overflow-hidden">
                        <div className={`h-full ${
                          sc === "read"  ? "bg-blue-500" :
                          sc === "write" ? "bg-amber-500" : "bg-red-500"
                        }`} style={{ width: `${pct}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top usuarios denegados */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 lg:col-span-1" data-testid="audit-top-users">
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                <Users size={14} className="text-[#007934]"/> Top 5 usuarios denegados
              </h3>
              <div className="space-y-2">
                {(stats.top_denied_users || []).map((u, i) => {
                  const max = Math.max(1, ...(stats.top_denied_users || []).map(x => x.count));
                  const pct = Math.round((u.count / max) * 100);
                  return (
                    <div key={i} className="text-xs" data-testid={`audit-top-user-${i}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <button
                          onClick={() => setUserFilter(u.user_email)}
                          className="font-medium text-gray-800 truncate hover:text-[#007934] hover:underline text-left"
                          title="Filtrar por este usuario"
                        >
                          {u.user_name} · <span className="text-gray-500">{u.user_email}</span>
                        </button>
                        <span className="text-red-600 font-bold ml-2">{fmtInt(u.count)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
                        <div className="h-full bg-red-500" style={{ width: `${pct}%` }}/>
                      </div>
                    </div>
                  );
                })}
                {(stats.top_denied_users || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic py-3 text-center">Sin denegaciones en el período.</p>
                )}
              </div>
            </div>

            {/* Top módulos denegados */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 lg:col-span-1" data-testid="audit-top-modules">
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                <Lock size={14} className="text-[#007934]"/> Módulos más denegados
              </h3>
              <div className="space-y-2">
                {(stats.top_denied_modules || []).map((m, i) => {
                  const max = Math.max(1, ...(stats.top_denied_modules || []).map(x => x.count));
                  const pct = Math.round((m.count / max) * 100);
                  return (
                    <div key={i} className="text-xs" data-testid={`audit-top-module-${i}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <button
                          onClick={() => setModuleFilter(m.module_id)}
                          className="font-medium text-gray-800 truncate hover:text-[#007934] hover:underline text-left flex items-center gap-1.5"
                          title="Filtrar por este módulo"
                        >
                          {m.module_id}
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${SCOPE_BG[m.scope] || "bg-gray-50 text-gray-600"}`}>{m.scope}</span>
                        </button>
                        <span className="text-red-600 font-bold ml-2">{fmtInt(m.count)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
                        <div className="h-full bg-red-500" style={{ width: `${pct}%` }}/>
                      </div>
                    </div>
                  );
                })}
                {(stats.top_denied_modules || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic py-3 text-center">Sin denegaciones en el período.</p>
                )}
              </div>
            </div>
          </div>

          {/* Evolución diaria */}
          <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="audit-daily-chart">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
              <Activity size={14} className="text-[#007934]"/> Evolución diaria · {days} días
              <span className="ml-auto text-[10px] flex items-center gap-3 text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-sm inline-block"/>concedidos</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm inline-block"/>denegados</span>
              </span>
            </h3>
            <div className="flex items-end gap-0.5 h-32 overflow-x-auto py-2">
              {(stats.daily || []).map((d, i) => {
                const grantedH = (d.granted / maxDailyEvent) * 100;
                const deniedH  = (d.denied  / maxDailyEvent) * 100;
                const isLast = i === (stats.daily || []).length - 1;
                return (
                  <div key={d.date} className="flex flex-col items-center min-w-[18px] group" title={`${d.date} · ${d.granted} concedidos · ${d.denied} denegados`}>
                    <div className="flex items-end gap-px h-full w-full">
                      <div
                        className="flex-1 bg-green-500/80 hover:bg-green-600 transition-colors"
                        style={{ height: d.granted > 0 ? `max(2px, ${grantedH}%)` : "0%" }}
                      />
                      <div
                        className="flex-1 bg-red-500/80 hover:bg-red-600 transition-colors"
                        style={{ height: d.denied  > 0 ? `max(2px, ${deniedH}%)` : "0%" }}
                      />
                    </div>
                    {(isLast || i % Math.ceil(days / 10) === 0) && (
                      <span className="text-[8px] text-gray-400 mt-1 truncate font-mono">{d.date.slice(5)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Histórico de envíos del email semanal */}
      <EmailHistory />

      {/* Histórico de alertas de seguridad (umbral 5 denegaciones / 24h) */}
      <AlertsHistory />

      {/* Tabla detallada con filtros */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="audit-detail-table">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
            <Filter size={14} className="text-[#007934]"/> Eventos detallados ({fmtInt(items.length)} mostrados)
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={grantedFilter}
              onChange={(e) => setGrantedFilter(e.target.value)}
              data-testid="audit-granted-filter"
              className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#007934]"
            >
              <option value="all">Todos</option>
              <option value="granted">Sólo concedidos</option>
              <option value="denied">Sólo denegados</option>
            </select>
            {(userFilter || moduleFilter) && (
              <button
                onClick={() => { setUserFilter(""); setModuleFilter(""); }}
                className="text-xs text-gray-500 hover:text-red-600 underline"
                data-testid="audit-clear-filters"
              >
                Limpiar filtros ({[userFilter, moduleFilter].filter(Boolean).length})
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => <div key={i} className="h-10 rounded loading-shimmer"/>)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            <Loader2 size={20} className="mx-auto mb-2 text-gray-300 animate-spin opacity-50"/>
            Sin eventos para los filtros seleccionados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase text-gray-400 tracking-wider border-b border-gray-100">
                  <th className="text-left py-2 pr-3">Fecha</th>
                  <th className="text-left py-2 pr-3">Usuario</th>
                  <th className="text-left py-2 pr-3">Rol</th>
                  <th className="text-left py-2 pr-3">Módulo</th>
                  <th className="text-left py-2 pr-3">Scope</th>
                  <th className="text-left py-2 pr-3">Resultado</th>
                  <th className="text-left py-2">Detalle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((it) => (
                  <tr key={it.id} className="hover:bg-gray-50">
                    <td className="py-2 pr-3 text-gray-500 font-mono text-[10px]">{it.created_at?.slice(0,16).replace("T"," ")}</td>
                    <td className="py-2 pr-3">
                      <button
                        onClick={() => setUserFilter(it.user_email)}
                        className="hover:text-[#007934] hover:underline text-left"
                      >
                        <span className="font-medium text-gray-800">{it.user_name || "—"}</span>
                        <span className="block text-[10px] text-gray-400">{it.user_email}</span>
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-gray-600">{it.user_role || "—"}</td>
                    <td className="py-2 pr-3">
                      <button
                        onClick={() => setModuleFilter(it.module_id)}
                        className="font-medium text-gray-800 hover:text-[#007934] hover:underline"
                      >
                        {it.module_id}
                      </button>
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${SCOPE_BG[it.scope] || "bg-gray-50 text-gray-600"}`}>{it.scope}</span>
                    </td>
                    <td className="py-2 pr-3">
                      {it.granted ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-green-700"><CheckCircle size={11}/>Concedido</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-red-700"><AlertTriangle size={11}/>Denegado</span>
                      )}
                    </td>
                    <td className="py-2 text-gray-500 truncate max-w-[280px]" title={it.detail}>{it.detail || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, color, bg, label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
        <Icon size={18} className={color}/>
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900 font-['Outfit']">{value}</p>
      </div>
    </div>
  );
}

/** Tarjeta con el histórico de alertas automáticas de denegaciones. */
function AlertsHistory() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ threshold: 5, cooldown_hours: 24 });
  const [loaded, setLoaded] = useState(false);
  const [running, setRunning] = useState(false);

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/audit-log/alerts-history?limit=10`);
      setItems(data.items || []);
      setMeta({ threshold: data.threshold, cooldown_hours: data.cooldown_hours });
    } catch (e) {
      // ignorar
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { reload(); }, []);

  const triggerCheck = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/audit-log/check-suspicious`);
      const sent = data.alerts_sent || 0;
      const found = data.candidates_found || 0;
      alert(sent
        ? `Se enviaron ${sent} alertas (${found} candidatos detectados, umbral ${data.threshold} denegaciones/24h).`
        : `No hay alertas que enviar ahora.\n${found} candidato(s) en el umbral, pero ya se notificaron en las últimas ${meta.cooldown_hours}h o no superan ${data.threshold} denegaciones.`);
      reload();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-red-50/30 border border-red-100 rounded-xl p-4" data-testid="audit-alerts-history">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-red-800 flex items-center gap-1.5">
          <AlertTriangle size={14} /> Alertas automáticas · {meta.threshold} denegaciones / 24h
        </h3>
        <button
          onClick={triggerCheck}
          disabled={running}
          data-testid="audit-check-suspicious"
          className="text-xs px-3 py-1.5 rounded-lg border border-red-300 bg-white text-red-700 hover:bg-red-50 flex items-center gap-1.5 disabled:opacity-50"
          title={`Comprueba ahora si hay usuarios con ≥ ${meta.threshold} denegaciones en las últimas 24h y envía alertas (cooldown ${meta.cooldown_hours}h por usuario)`}
        >
          {running ? <Loader2 size={11} className="animate-spin"/> : <Shield size={11}/>} Comprobar ahora
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        Cada hora, el sistema escanea el audit log: si un usuario acumula <strong>{meta.threshold}+ denegaciones en 24h</strong>,
        se envía un email de alerta a David Redondo. <strong>Cooldown de {meta.cooldown_hours}h</strong> por usuario para evitar spam.
      </p>
      {!loaded ? (
        <div className="h-8 rounded loading-shimmer"/>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Sin alertas registradas. Buena señal: nadie superó el umbral.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((it) => (
            <div key={it.id} className="text-[11px] flex items-center justify-between bg-white rounded border border-red-100 px-3 py-1.5" data-testid={`alert-history-${it.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <AlertTriangle size={11} className="text-red-600 flex-shrink-0"/>
                <span className="text-gray-600 font-mono">{it.alerted_at?.slice(0,16).replace("T", " ")}</span>
                <span className="text-gray-400">·</span>
                <span className="font-medium text-gray-800 truncate">{it.user_name}</span>
                <span className="text-gray-500 truncate">({it.user_email})</span>
              </div>
              <span className="text-red-600 font-bold flex-shrink-0">{it.denied_count} denegaciones</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Tarjeta con el histórico de envíos del email semanal de audit log. */
function EmailHistory() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/audit-log/email-history?limit=10`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <div className="bg-purple-50/30 border border-purple-100 rounded-xl p-4" data-testid="audit-email-history">
      <h3 className="text-sm font-bold text-purple-800 mb-2 flex items-center gap-1.5">
        <Mail size={14} /> Envíos automáticos semanales · Lunes 8:10 UTC
      </h3>
      <p className="text-[11px] text-gray-500 mb-3">
        Cada lunes a las 8:10 UTC, el sistema genera el XLSX del audit log de los últimos 7 días y lo envía a
        <strong className="text-gray-700"> David Redondo · dredondo@valorizasm.com</strong> vía Mandrill (Mailchimp Transactional).
        El correo incluye un resumen HTML con KPIs + el dossier completo como adjunto.
      </p>
      {!loaded ? (
        <div className="h-8 rounded loading-shimmer"/>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Sin envíos registrados aún. Pulsa <strong>"Enviar semanal"</strong> arriba para hacer una primera prueba.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((it) => (
            <div key={it.id} className="text-[11px] flex items-center justify-between bg-white rounded border border-purple-100 px-3 py-1.5" data-testid={`email-history-${it.id}`}>
              <div className="flex items-center gap-2">
                <CheckCircle size={11} className="text-green-600 flex-shrink-0"/>
                <span className="text-gray-600 font-mono">{it.sent_at?.slice(0,16).replace("T", " ")}</span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-700">{it.recipient}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <span>{it.rows} eventos</span>
                {it.denied_events > 0 && <span className="text-red-600 font-bold">{it.denied_events} denegados</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
